// library imports
import * as React from "react";
import {
  Modal,
  Grid,
  TextField,
  InputLabel,
  Radio,
  Checkbox,
  Select,
} from "@mui/material";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import InputMask from "react-input-mask";


// helper import
import globals from "../globals/globals";
import { getHeaders } from "../utils/HeaderHelper";
import { getYodaDateToday } from "../utils/DateHelper";
import {
  MAX_NAME_LENGTH,
  getNameError,
  MAX_EMAIL_LENGTH,
  getEmailError,
  MAX_ADDRESS_LENGTH,
  getAddressError,
  getAddressLine2Error,
  ZIP_LENGTH,
  sanitizeZip,
  getZipError,
  ZIP_EXT_LENGTH,
  sanitizeZipExtension,
  getZipExtensionError,
  sanitizePhoneNumber,
  getPhoneNumberError,
  MAX_CITY_LENGTH,
  getCityError,
} from "../utils/DemographicValidationHelper";

// app setting imports
import { AuthenticationContext } from "../Context";
import GeneralModal from "../modals/GeneralModal";
import { Stack } from "@mui/system";
import { dateToString, formatToUsDateFromUTCNew } from "../utils/DateHelper";
import { useFlash } from "../FlashContext";

interface Props {
  manualOpen: boolean;
  setManualOpen: any;
}

export const stateOptions = globals.states.map((state) => {
  return (
    <option key={state} value={state}>
      {state}
    </option>
  );
});

const normalizeForSave = (value?: string): string => {
  return value
    ?.trim()
    .replace(/\s+/g, " ")
    ?? "";
};

const DOB_INPUT_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const parseDobInput = (dobInputValue: string): Date | null => {
  if (!DOB_INPUT_REGEX.test(dobInputValue)) {
    return null;
  }

  const [monthStr, dayStr, yearStr] = dobInputValue.split("/");
  const month = Number(monthStr);
  const day = Number(dayStr);
  const year = Number(yearStr);

  if (!month || !day || !year) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);
  const isSameCalendarDate =
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getDate() === day;

  return isSameCalendarDate ? parsedDate : null;
};

const AddPatientManual: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);

  // form fields
  const [firstName, setFirstName] = React.useState<string>("");
  const [middleName, setMiddleName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [dateOfBirth, setDateOfBirth] = React.useState<Date | null>(null);
  const [dobInputValue, setDobInputValue] = React.useState<string>("");

  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const [homePhoneNumber, setHomePhoneNumber] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [noEmail, setNoEmail] = React.useState<boolean>(false);
  const [gender, setGender] = React.useState<string>("Female");
  const [address, setAddress] = React.useState<string>("");
  const [addressLine2, setAddressLine2] = React.useState<string>("");
  const [city, setCity] = React.useState<string>("");
  const [state, setState] = React.useState<string>("");
  const [zip, setZip] = React.useState<string>("");
  const [zipExtension, setZipExtension] = React.useState<string>("");
  const [contactChannel, setContactChannel] =
    React.useState<string>("mobile_phone");
  const [contactMethod, setContactMethod] = React.useState<string>("call");

  const { showToast } = useFlash();
  const [unsavedchanges, setUnsavedChanges] = React.useState<boolean>(false);
  const [loading, setIsLoading] = React.useState<boolean>(false);
  const [showValidationErrors, setShowValidationErrors] =
    React.useState<boolean>(false);

  const handleModalClose = () => {
    setShowValidationErrors(false);
    props.setManualOpen(false);
  };

  const handleDateChange = (date: Date | null, keyboardInputValue?: string) => {
    setDateOfBirth(date);
    if (typeof keyboardInputValue === "string") {
      setDobInputValue(keyboardInputValue);
    } else if (date) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = String(date.getFullYear());
      setDobInputValue(`${month}/${day}/${year}`);
    } else {
      setDobInputValue("");
    }
    setUnsavedChanges(true);
  };

  const saveManualPatient = () => {
    setShowValidationErrors(true);
    if (validForm()) {
      const sanitizedZip = zip?.trim();
      const sanitizedZipExtension = zipExtension ? zipExtension.trim() : "";
      const zipCode = (sanitizedZip + sanitizedZipExtension).trim();

      setIsLoading(true);

      fetch(`/add_manual_patient`, {
        method: "POST",
        headers: getHeaders(authenticationSetting?.csrfToken),
        body: JSON.stringify({
          patient: {
            first_name: normalizeForSave(firstName),
            middle_name: normalizeForSave(middleName),
            last_name: normalizeForSave(lastName),
            email: normalizeForSave(email),
            no_email: noEmail,
            date_of_birth: dateOfBirth,
            mobile_phone_number: sanitizePhoneNumber(phoneNumber),
            gender: gender,
            address_line_1: normalizeForSave(address),
            address_line_2: normalizeForSave(addressLine2),
            city: normalizeForSave(city),
            state: normalizeForSave(state),
            zip: zipCode,
            contact_channel: contactChannel,
            contact_method: contactMethod,
            home_phone_number: sanitizePhoneNumber(homePhoneNumber),
          },
        }),
      })
        .then(async (response) => {
          const result = await response.json();
          return { ok: response.ok, result };
        })
        .then(({ ok, result }) => {
          if (!ok || result?.success !== true) {
            console.log(result.message);
            showToast({
              title: "Failed to Add Patient",
              text: result?.message || "Please correct the highlighted fields and try again.",
              type: "error",
            });
          } else {
            showToast({ title: "Patient Added Successfully", text: result.message, type: "success" });
            let url = "/patients";
            location.href = url;
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const validForm = () => {
    let errorFields = [];
    const sanitizedZip = zip?.trim();
    const sanitizedZipExtension = zipExtension ? zipExtension.trim() : "";
    const parsedDob = parseDobInput(dobInputValue);
    const today = new Date(getYodaDateToday());
    const isFutureDob = parsedDob ? parsedDob > today : false;

    if (firstName?.length < 1) {
      errorFields.push("First Name");
    }
    if (lastName?.length < 1) {
      errorFields.push("Last Name");
    }
    if (!dobInputValue) {
      errorFields.push("Date Of Birth");
    } else if (!parsedDob || isFutureDob) {
      errorFields.push("Date Of Birth is invalid");
    }
    if (!noEmail) {
      if (email?.length < 1) {
        errorFields.push("Email");
      }
    }
    if (address?.length < 1) {
      errorFields.push("Address");
    }
    if (city?.length < 1) {
      errorFields.push("City");
    }
    if (state?.length < 1) {
      errorFields.push("State");
    }
    if (zip?.length < 1) {
      errorFields.push("Zip");
    } else if (!/^\d{5}$/.test(sanitizedZip)) {
      errorFields.push("ZIP Code must be exactly 5 digits");
    } else if (
      sanitizedZipExtension &&
      !/^\d{4}$/.test(sanitizedZipExtension)
    ) {
      errorFields.push("ZIP Extension must be exactly 4 digits");
    }

    if (errorFields.length > 0) {
      const message =
        errorFields.length === 1 && errorFields[0] === "Date Of Birth"
          ? "Date of Birth is required."
          : errorFields.length === 1 && errorFields[0] === "Date Of Birth is invalid"
            ? "Please enter a valid Date of Birth in MM/DD/YYYY format."
          : `${errorFields.join(", ")} are required.`;
      showToast({ title: "Invalid Form Fields", text: message, type: "error" });
      return false;
    } else {
      setDateOfBirth(parsedDob);
      return true;
    }
  };

  React.useEffect(() => {
    if (contactChannel === "mobile_phone" || contactChannel === "home_phone") {
      setContactMethod("call");
    }
    if (contactChannel === "email") {
      setContactMethod("email");
    }
  }, [contactChannel]);

  // no email handler
  const handleNoEmailCheck = () => {
    setNoEmail(!noEmail);
  };

  const parsedDobForDisplay = parseDobInput(dobInputValue);
  const dobErrorText =
    !showValidationErrors
      ? null
      : !dobInputValue
        ? "Date of Birth is required."
        : !parsedDobForDisplay
          ? "Enter DOB in MM/DD/YYYY format."
          : parsedDobForDisplay > new Date(getYodaDateToday())
            ? "Date of Birth cannot be in the future."
            : null;

  return (
    <GeneralModal
      open={props.manualOpen}
      title={"Add Patient to Starfield"}
      successCallback={saveManualPatient}
      closeCallback={handleModalClose}
      containerClassName="manual-patient-modal-body"
      width="900px"
      isLoading={loading}
    >
      <Grid container className="form-container" spacing={1}>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="first_name" className="field-label2">
                First Name*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="firstName"
                size="small"
                required
                value={firstName}
                variant="outlined"
                className="the-field"
                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                helperText={getNameError(
                  firstName,
                  "First name",
                  showValidationErrors
                )}
                error={
                  !!getNameError(firstName, "First name", showValidationErrors)
                }
                onChange={(event) =>
                  setFirstName((event.target.value))
                }
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="middle_name" className="field-label2">
                Middle Name
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="middle_name"
                size="small"
                value={middleName}
                className="the-field"
                variant="outlined"
                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                helperText={getNameError(
                  middleName,
                  "Middle name",
                  showValidationErrors
                )}
                error={
                  !!getNameError(
                    middleName,
                    "Middle name",
                    showValidationErrors
                  )
                }
                onChange={(event) => {
                  setMiddleName((event.target.value));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="last_name" className="field-label2">
                Last Name*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="last_name"
                size="small"
                value={lastName}
                required
                className="the-field"
                variant="outlined"
                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                helperText={getNameError(
                  lastName,
                  "Last name",
                  showValidationErrors
                )}
                error={
                  !!getNameError(lastName, "Last name", showValidationErrors)
                }
                onChange={(event) => {
                  setLastName((event.target.value));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="date_of_birth" className="field-label2">
                Date of Birth*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="date-field-container">
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  disableToolbar
                  autoOk={true}
                  variant="inline"
                  format="MM/dd/yyyy"
                  maxDate={new Date(getYodaDateToday())}
                  margin="normal"
                  id="date-picker-inline"
                  value={
                    dateOfBirth ? formatToUsDateFromUTCNew(dateOfBirth) : null
                  }
                  onChange={handleDateChange}
                  className="date-field patient-dob-field"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  error={!!dobErrorText}
                  helperText={dobErrorText}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="contactChannel" className="field-label2">
                Preferred Contact Channel*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <div className="row" style={{ height: 40 }}>
                <Radio
                  checked={contactChannel === "mobile_phone"}
                  onChange={(event) => {
                    setContactChannel(event.target.value);
                    setUnsavedChanges(true);
                  }}
                  value="mobile_phone"
                  name="radio-buttons"
                  inputProps={{ "aria-label": "mobile_phone" }}
                  style={{ backgroundColor: "transparent" }}
                  sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                />
                <p className="radio-label">Mobile Phone</p>
                <Radio
                  checked={contactChannel === "home_phone"}
                  onChange={(event) => {
                    setContactChannel(event.target.value);
                    setUnsavedChanges(true);
                  }}
                  value="home_phone"
                  name="radio-buttons"
                  inputProps={{ "aria-label": "home_phone" }}
                  style={{ backgroundColor: "transparent" }}
                  sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                />
                <p className="radio-label">Home Phone</p>
                <Radio
                  checked={contactChannel === "email"}
                  onChange={(event) => {
                    setContactChannel(event.target.value);
                    setUnsavedChanges(true);
                  }}
                  value="email"
                  name="radio-buttons"
                  inputProps={{ "aria-label": "email" }}
                  style={{ backgroundColor: "transparent" }}
                  sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                />
                <p className="radio-label">Email</p>
              </div>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="contactMethod" className="field-label2">
                Preferred Contact Method*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              {contactChannel === "mobile_phone" ? (
                <div className="row" style={{ height: 40 }}>
                  <Radio
                    checked={contactMethod === "call"}
                    onChange={(event) => {
                      setContactMethod(event.target.value);
                      setUnsavedChanges(true);
                    }}
                    value="call"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "call" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                    Call
                  </p>

                  <Radio
                    checked={contactMethod === "text"}
                    onChange={(event) => {
                      setContactMethod(event.target.value);
                      setUnsavedChanges(true);
                    }}
                    value="text"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "text" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                    Text
                  </p>
                </div>
              ) : contactChannel === "home_phone" ? (
                <div className="row" style={{ height: 40 }}>
                  <Radio
                    checked={contactMethod === "call"}
                    onChange={(event) => {
                      setContactMethod(event.target.value);
                      setUnsavedChanges(true);
                    }}
                    value="call"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "call" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                    Call
                  </p>
                </div>
              ) : contactChannel === "email" ? (
                <div className="row" style={{ height: 40 }}>
                  <Radio
                    checked={contactMethod === "email"}
                    onChange={(event) => {
                      setContactMethod(event.target.value);
                      setUnsavedChanges(true);
                    }}
                    value="email"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "email" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label">Email</p>
                </div>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="Phone Number" className="field-label2">
                Mobile Phone Number
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <InputMask
                style={{ borderBottom: "none" }}
                id="mobile-phone-number"
                mask="(999) 999-9999"
                value={phoneNumber}
                maskChar=""
                className="the-field"
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setUnsavedChanges(true);
                }}
              >
                {() => (
                  <TextField
                    className="the-field"
                    variant="outlined"
                    size="small"
                    {...getPhoneNumberError(
                      sanitizePhoneNumber(phoneNumber),
                      showValidationErrors
                    )}
                  />
                )}
              </InputMask>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="homePhoneNumber" className="field-label2">
                Home Phone Number
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <InputMask
                style={{ borderBottom: "none" }}
                id="homePhoneNumber"
                mask="(999) 999-9999"
                value={homePhoneNumber}
                maskChar=""
                className="the-field"
                onChange={(e) => {
                  setHomePhoneNumber(e.target.value);
                  setUnsavedChanges(true);
                }}
              >
                {() => (
                  <TextField
                    className="the-field"
                    variant="outlined"
                    size="small"
                    {...getPhoneNumberError(
                      sanitizePhoneNumber(homePhoneNumber),
                      showValidationErrors
                    )}
                  />
                )}
              </InputMask>
            </Grid>
          </Grid>

          <Grid container>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              width={"100%"}
            >
              <InputLabel htmlFor="email" className="field-label2">
                Email*
              </InputLabel>
              <InputLabel
                htmlFor="no-email"
                className="field-label2"
                style={{
                  textAlign: "right",
                  marginRight: "15px",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={noEmail}
                  size="small"
                  inputProps={{ "aria-label": "Self" }}
                  style={{ padding: "0px" }}
                  onClick={handleNoEmailCheck}
                />
                Email not available
              </InputLabel>
            </Stack>
            <Grid item xs={12} className="field-container">
              {!noEmail && (
                <TextField
                  id="email-address"
                  size="small"
                  variant="outlined"
                  className="the-field"
                  inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                  helperText={getEmailError(email)}
                  error={!!getEmailError(email)}
                  required
                  value={email}
                  type="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="gender" className="field-label2">
                Gender*
              </InputLabel>
            </Grid>
            <Grid item xs={12} style={{ height: "40px" }}>
              <Radio
                checked={gender === "Male"}
                onChange={(event) => setGender(event.target.value)}
                value="Male"
                name="radio-buttons"
                inputProps={{ "aria-label": "Self" }}
                style={{ backgroundColor: "transparent" }}
              />
              Male
              <Radio
                checked={gender === "Female"}
                onChange={(event) => setGender(event.target.value)}
                value="Female"
                name="radio-buttons"
                inputProps={{ "aria-label": "Spouse" }}
                style={{ backgroundColor: "transparent" }}
              />
              Female
              <Radio
                checked={gender === "Unknown"}
                onChange={(event) => setGender(event.target.value)}
                value="Unknown"
                name="radio-buttons"
                inputProps={{ "aria-label": "Unknown" }}
                style={{ backgroundColor: "transparent" }}
              />
              Unknown
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="address" className="field-label2">
                Address*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="address"
                size="small"
                value={address}
                required
                variant="outlined"
                className="the-field"
                inputProps={{
                  maxLength: MAX_ADDRESS_LENGTH,
                  inputMode: "text",
                }}
                {...getAddressError(address, showValidationErrors)}
                onChange={(event) => {
                  setAddress(event.target.value.slice(0, MAX_ADDRESS_LENGTH));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="addressLine2" className="field-label2">
                Address Line Two
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="addressLine2"
                size="small"
                value={addressLine2}
                variant="outlined"
                className="the-field"
                inputProps={{
                  maxLength: MAX_ADDRESS_LENGTH,
                  inputMode: "text",
                }}
                {...getAddressLine2Error(addressLine2, showValidationErrors)}
                onChange={(e) => {
                  setAddressLine2(e.target.value.slice(0, MAX_ADDRESS_LENGTH));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="city" className="field-label2">
                City*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="city"
                size="small"
                value={city}
                required
                variant="outlined"
                className="the-field"
                inputProps={{ maxLength: MAX_CITY_LENGTH }}
                {...getCityError(city, showValidationErrors)}
                onChange={(e) => {
                  setCity(e.target.value.slice(0, MAX_CITY_LENGTH));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="state" className="field-label2">
                State*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <Select
                id="state"
                value={state}
                required
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setState(event.target.value as string);
                }}
                native
              >
                <option value={""}>Select State</option>
                {stateOptions}
              </Select>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="zip" className="field-label2">
                Zip*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="zip"
                size="small"
                value={zip}
                required
                variant="outlined"
                className="the-field"
                inputProps={{
                  maxLength: ZIP_LENGTH,
                  inputMode: "numeric",
                }}
                {...getZipError(zip, showValidationErrors)}
                onChange={(event) => {
                  setZip(sanitizeZip(event.target.value));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="zipExtension" className="field-label2">
                Zip Extension
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="zipExtension"
                size="small"
                value={zipExtension}
                variant="outlined"
                className="the-field"
                inputProps={{
                  maxLength: ZIP_EXT_LENGTH,
                  inputMode: "numeric",
                }}
                {...getZipExtensionError(zipExtension, showValidationErrors)}
                onChange={(e) => {
                  setZipExtension(sanitizeZipExtension(e.target.value));
                  setUnsavedChanges(true);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </GeneralModal>
  );
};

export default AddPatientManual;
